import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/redux';

import { Box, Typography, Link } from '@mui/material';
import {
  BrandLogo,
  Loader,
  PageWrapper,
  PositionSubscriptionForm,
  TextButton,
} from '../../components';

import {
  getSubscriptionDetails,
  positionSubscriptionSelectors,
  updatePositionSubscription,
} from 'src/redux/positionSubscription';

import { isStatusIdle, isStatusLoading } from 'src/utils';
import { PositionSubscriptionFields } from 'src/types';
import {
  APP_ROUTES,
  PositionSubscriptionFrequency,
  PositionSubscriptionFrequencyOptions,
  PositionSubscriptionType,
} from 'src/constants';

import { createCandidatePositionSubscriptionValidationSchema } from '../PositionSubscriptionCandidate/validationsSchema';
import { createReferralPositionSubscriptionValidationSchema } from '../PositionSubscriptionReferral/validationsSchema';

export const PositionSubscriptionSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { apiStatus: subscriptionDetailsAPIStatus, data: subscriptionDetails } =
    useAppSelector(positionSubscriptionSelectors.getSubscriptionDetailsAPIData);
  const updatePositionSubscriptionAPIStatus = useAppSelector(
    positionSubscriptionSelectors.getUpdateSubscriptionAPIStatus,
  );

  const token = searchParams.get('token');
  const frequency = searchParams.get('frequency');

  useEffect(() => {
    if (token) {
      dispatch(getSubscriptionDetails(token));
    }
  }, []);

  useEffect(() => {
    const isValidFrequencyValue = PositionSubscriptionFrequencyOptions.some(
      (fo) => fo.value === frequency,
    );
    if (
      token &&
      subscriptionDetails?.type === PositionSubscriptionType.Referral &&
      isValidFrequencyValue
    ) {
      dispatch(
        updatePositionSubscription({
          token,
          frequency: frequency as PositionSubscriptionFrequency,
        }),
      );
    }
  }, [subscriptionDetails?.type, token, frequency]);

  const initialValues: PositionSubscriptionFields = useMemo(
    () => ({
      email: subscriptionDetails?.email,
      name: subscriptionDetails?.name,
      locations: subscriptionDetails?.locations,
      technicalFlows: subscriptionDetails?.technicalFlows,
      experience: subscriptionDetails?.experience,
      frequency: subscriptionDetails?.frequency,
    }),
    [subscriptionDetails],
  );
  const disabledFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () => ['email'],
    [],
  );

  const requiredFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () =>
      subscriptionDetails?.type === PositionSubscriptionType.Candidate
        ? ['email', 'name', 'locations', 'technicalFlows', 'experience']
        : subscriptionDetails?.type === PositionSubscriptionType.Referral
        ? ['email', 'frequency']
        : [],
    [subscriptionDetails],
  );

  const onPositionSubscriptionFormSubmit = useCallback(
    (values: PositionSubscriptionFields) => {
      if (token) {
        dispatch(updatePositionSubscription({ ...values, token }));
      }
    },
    [token],
  );

  const onUnsubscribePositionsClick = useCallback(() => {
    if (token) {
      const params = new URLSearchParams();
      params.set('token', token);

      navigate(
        `/${
          APP_ROUTES.POSITION_SUBSCRIPTION_UNSUBSCRIBED
        }?${params.toString()}`,
      );
    }
  }, [token]);

  const onSendFeedbackClick = useCallback(() => {
    window.open(
      'https://docs.google.com/forms/d/1HhsAo1_kmFdOG3h7ZjAXpp_Do7d6ZXinBoswXaGL0GM/viewform',
      '_blank',
      'noreferrer',
    );
  }, [token]);

  if (
    isStatusLoading(subscriptionDetailsAPIStatus) ||
    isStatusIdle(subscriptionDetailsAPIStatus)
  ) {
    return (
      <PageWrapper>
        <Loader showBrandLogo />
      </PageWrapper>
    );
  }

  if (!subscriptionDetails) {
    return (
      <PageWrapper>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
          data-testid="is-loading"
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              m: 6,
            }}
          >
            <BrandLogo />
          </Box>
          <Typography variant="h6" fontWeight="600" textAlign="center">
            Subscription not found
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.background.backgroundPrimary,
        minWidth: '100%',
        minHeight: '100%',
        padding: '41px 16px',
        [theme.breakpoints.down('lg')]: {
          paddingTop: '130px',
        },
      })}
    >
      <Box sx={{ position: 'absolute', top: '49px', left: '66px' }}>
        <BrandLogo width={133} />
      </Box>
      <Box display="flex" alignItems="center" flexDirection="column">
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            borderRadius: '48px',
            width: '603px',
            position: 'relative',
          })}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '62px 100px',
              width: '100%',
              gap: '32px',
            }}
          >
            <PositionSubscriptionForm
              title={
                <>
                  Get notified about our
                  <br />
                  newest positions
                </>
              }
              initialValues={initialValues}
              disabledFields={disabledFields}
              requiredFields={requiredFields}
              validationSchema={
                subscriptionDetails.type === PositionSubscriptionType.Candidate
                  ? createCandidatePositionSubscriptionValidationSchema
                  : createReferralPositionSubscriptionValidationSchema
              }
              type={subscriptionDetails.type}
              successMessage="You’ve updated your subscription."
              submissionBtnLabel="Update subscription"
              apiStatus={updatePositionSubscriptionAPIStatus}
              onSubmit={onPositionSubscriptionFormSubmit}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent={
                subscriptionDetails.type === PositionSubscriptionType.Referral
                  ? 'space-between'
                  : 'center'
              }
              gap={2}
              width="100%"
            >
              <TextButton
                onClick={onUnsubscribePositionsClick}
                variant="primary"
              >
                Unsubscribe
              </TextButton>
              {subscriptionDetails.type ===
                PositionSubscriptionType.Referral && (
                <TextButton onClick={onSendFeedbackClick} variant="primary">
                  Send Feedback
                </TextButton>
              )}
            </Box>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" mt={6}>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link component={RouterLink} to="/terms-of-use">
              Terms of use
            </Link>
          </Typography>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link href="https://agileengine.com/privacy-policy" target="_blank">
              Privacy policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
